'use client';

import React, { useEffect, useRef } from 'react';

interface DesktopSnapScrollProps {
  children: React.ReactNode;
  maxSections?: number;
}

export default function DesktopSnapScroll({ children, maxSections }: DesktopSnapScrollProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isScrolling = useRef(false);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const currentSectionRef = useRef(0);
  const lastSectionChangeTime = useRef(0);
  const wheelDebounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const lastScrollDirection = useRef<'up' | 'down' | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Only enable on desktop
    const isDesktop = () => window.innerWidth >= 768;
    if (!isDesktop()) return;

    const mainElement = container.querySelector('main');
    if (!mainElement) return;
    
    const allSections = Array.from(mainElement.children) as HTMLElement[];
    // Limit sections to maxSections if provided
    const sections = maxSections ? allSections.slice(0, maxSections) : allSections;

    const snapToSection = (index: number) => {
      if (index < 0 || index >= sections.length) return;
      if (!isDesktop()) return;
      if (isScrolling.current) return;
      
      // Check if enough time has passed since last section change
      const now = Date.now();
      const timeSinceLastChange = now - lastSectionChangeTime.current;
      if (timeSinceLastChange < 300) {
        return;
      }
      
      isScrolling.current = true;
      currentSectionRef.current = index;
      lastSectionChangeTime.current = now;
      
      sections[index].scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });

      // Dispatch scroll event to ensure header detects scroll direction
      const scrollEvent = new Event('scroll', { bubbles: true });
      container.dispatchEvent(scrollEvent);

      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      scrollTimeout.current = setTimeout(() => {
        isScrolling.current = false;
        container.dispatchEvent(new Event('scroll', { bubbles: true }));
      }, 500);
    };

    const handleWheel = (e: WheelEvent) => {
      if (!isDesktop()) return;
      
      const scrollDirection = e.deltaY > 0 ? 'down' : 'up';
      
      // Only prevent default if we're actively snapping to avoid interfering with scroll-based animations
      if (isScrolling.current) {
        // Allow some scroll events to pass through even during snap to support animations
        return;
      }

      // Batch all layout reads together at the start to avoid forced reflows
      // Read all layout properties synchronously before any potential DOM writes
      const scrollTop = container.scrollTop;
      const viewportHeight = window.innerHeight;
      let lastSnapSectionBottom = 0;
      
      // Check if we're on the last snap section and scrolling down - allow normal scrolling
      if (maxSections && sections.length > 0) {
        const lastSnapSection = sections[sections.length - 1];
        // Batch offsetTop and offsetHeight reads together
        const lastSnapSectionTop = lastSnapSection.offsetTop;
        const lastSnapSectionHeight = lastSnapSection.offsetHeight;
        lastSnapSectionBottom = lastSnapSectionTop + lastSnapSectionHeight;
        const isOnLastSnapSection = currentSectionRef.current === sections.length - 1;
        
        // If on the last snap section and scrolling down, allow normal scrolling
        if (isOnLastSnapSection && scrollDirection === 'down') {
          return; // Don't prevent default, allow normal scroll
        }
        
        // If scrolled past the last snap section, allow normal scrolling
        if (scrollTop + viewportHeight > lastSnapSectionBottom) {
          return; // Don't prevent default, allow normal scroll
        }
      }

      e.preventDefault();
      
      // Clear any pending debounce timeout
      if (wheelDebounceTimeout.current) {
        clearTimeout(wheelDebounceTimeout.current);
      }

      // Debounce wheel events for desktop
      wheelDebounceTimeout.current = setTimeout(() => {
        const directionChanged = lastScrollDirection.current !== scrollDirection;
        
        if (directionChanged) {
          lastScrollDirection.current = scrollDirection;
          
          if (scrollDirection === 'down') {
            // Scrolling down - next section (but don't go beyond sections.length - 1)
            const nextIndex = currentSectionRef.current + 1;
            // Only snap if not already on the last section
            if (nextIndex < sections.length) {
              snapToSection(nextIndex);
            }
          } else {
            // Scrolling up - previous section
            snapToSection(Math.max(currentSectionRef.current - 1, 0));
          }
        } else {
          if (scrollDirection === 'down') {
            const nextIndex = currentSectionRef.current + 1;
            // Only snap if not already on the last section
            if (nextIndex < sections.length) {
              snapToSection(nextIndex);
            }
          } else {
            snapToSection(Math.max(currentSectionRef.current - 1, 0));
          }
        }
      }, 100);
    };

    // Find current section based on scroll position
    const updateCurrentSection = () => {
      if (!isDesktop()) return;
      
      // Batch all layout reads together to avoid forced reflows
      const scrollTop = container.scrollTop;
      const viewportHeight = window.innerHeight;
      
      // Read all section positions in one batch
      const sectionPositions = sections.map(section => ({
        top: section.offsetTop,
        height: section.offsetHeight,
        bottom: section.offsetTop + section.offsetHeight
      }));
      
      // Only check sections within the snap scroll limit
      sections.forEach((section, index) => {
        const { top, height } = sectionPositions[index];
        const viewportCenter = scrollTop + (viewportHeight / 2);
        
        if (viewportCenter >= top && viewportCenter < top + height) {
          currentSectionRef.current = index;
        }
      });
      
      // If scrolled past the last snap section, don't update currentSectionRef
      // This prevents snap scroll from trying to snap to sections beyond the limit
      if (maxSections && sections.length > 0) {
        const lastSectionPos = sectionPositions[sections.length - 1];
        if (scrollTop + (viewportHeight / 2) >= lastSectionPos.bottom) {
          // User has scrolled past the snap scroll area, allow normal scrolling
          return;
        }
      }
    };

    // Disable snap scroll behavior when scrolling past the maxSections limit
    const handleScroll = (e: Event) => {
      if (!isDesktop()) return;
      
      // Batch all layout reads together to avoid forced reflows
      const scrollTop = container.scrollTop;
      const viewportHeight = window.innerHeight;
      
      // If maxSections is set and we're past the last snap section, allow normal scrolling
      if (maxSections && sections.length > 0) {
        const lastSnapSection = sections[sections.length - 1];
        // Batch offsetTop and offsetHeight reads
        const lastSnapSectionTop = lastSnapSection.offsetTop;
        const lastSnapSectionHeight = lastSnapSection.offsetHeight;
        const lastSnapSectionBottom = lastSnapSectionTop + lastSnapSectionHeight;
        
        // If scrolled past the last snap section, disable snap scroll behavior
        if (scrollTop + viewportHeight > lastSnapSectionBottom) {
          // Allow normal scrolling past the snap sections
          // Don't prevent event propagation - allow scroll events for neuroplastic sections
          return;
        }
      }
      
      // Always allow scroll events to propagate for scroll-based animations
      updateCurrentSection();
    };

    // Handle window resize
    const handleResize = () => {
      if (!isDesktop()) {
        // If resized to mobile, disable snap scroll
        container.removeEventListener('wheel', handleWheel);
        container.removeEventListener('scroll', handleScroll);
      } else {
        // If resized to desktop, enable snap scroll
        container.addEventListener('wheel', handleWheel, { passive: false });
        container.addEventListener('scroll', handleScroll, { passive: true });
        updateCurrentSection();
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    // Initial section detection
    updateCurrentSection();

    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      if (wheelDebounceTimeout.current) {
        clearTimeout(wheelDebounceTimeout.current);
      }
    };
  }, [maxSections]);

  return (
    <div
      ref={containerRef}
      data-snap-scroll-container-desktop
      className="flex-1 overflow-y-auto overflow-x-hidden snap-y snap-mandatory w-full"
      style={{
        height: '100vh',
        maxHeight: '100vh',
        scrollBehavior: 'smooth',
        scrollSnapType: 'y mandatory',
        paddingTop: 0,
        marginTop: 0,
        overflowX: 'hidden',
        overscrollBehavior: 'none',
        overscrollBehaviorY: 'none',
      }}
    >
      {children}
    </div>
  );
}

